import { X, HelpCircle } from "lucide-react";

const Step = ({ n, children }) => (
  <li className="flex gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-[#0A1C40] text-white text-xs font-black flex items-center justify-center">{n}</span>
    <span className="text-sm leading-relaxed">{children}</span>
  </li>
);

function HelpShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-3" data-testid="help-modal">
      <div className="bg-white w-full max-w-lg rounded-t-xl sm:rounded-xl max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-[#0A1C40] text-white px-5 py-3 flex items-center justify-between">
          <div className="font-display font-black flex items-center gap-2"><HelpCircle className="w-5 h-5" /> {title}</div>
          <button onClick={onClose} data-testid="help-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function PosHelpModal({ onClose }) {
  return (
    <HelpShell title="How to use the register" onClose={onClose}>
      <div className="space-y-5">
        <div>
          <div className="ipv-eyebrow mb-2">Ring up a sale</div>
          <ol className="space-y-2">
            <Step n={1}>Tap products on the left to add them to the cart (or use the search box to find by name/SKU).</Step>
            <Step n={2}>Ask for the customer's <b>name and phone number</b> — the phone gets them a text receipt.</Step>
            <Step n={3}>Pick how they pay (below). Done — the sale shows up in "Today" at the top.</Step>
          </ol>
        </div>
        <div>
          <div className="ipv-eyebrow mb-2">💵 Cash</div>
          <ol className="space-y-2">
            <Step n={1}>Tap <b>Cash</b>, type the amount the customer handed you.</Step>
            <Step n={2}>The screen shows the <b>change to give back</b>. Confirm to finish.</Step>
          </ol>
        </div>
        <div>
          <div className="ipv-eyebrow mb-2">💳 Card · Tap</div>
          <ol className="space-y-2">
            <Step n={1}>Tap <b>Card · Tap</b> — it opens the Square app on this device to take the tap/chip payment.</Step>
            <Step n={2}>When Square finishes, you're brought back here and the sale is recorded.</Step>
          </ol>
        </div>
        <div>
          <div className="ipv-eyebrow mb-2">🔗 Send payment link (pay by phone)</div>
          <ol className="space-y-2">
            <Step n={1}>Great for phone orders or customers who want to pay later. Build the cart, tap <b>Send payment link</b>.</Step>
            <Step n={2}>Enter their name plus a <b>phone or email</b> — they get a secure link to pay from their own phone.</Step>
            <Step n={3}>The link works for <b>48 hours</b>. You'll see the order flip to PAID in Admin → Orders.</Step>
          </ol>
        </div>
        <div className="bg-[#FFF8E1] border border-[#FFC220] p-3 text-sm">
          <b>Rules:</b> Never give a discount or refund without a manager's PIN. If something looks wrong, stop and call the owner: <b>(616) 410-0158</b>.
        </div>
      </div>
    </HelpShell>
  );
}

export function DriverHelpModal({ onClose }) {
  return (
    <HelpShell title="How your delivery day works" onClose={onClose}>
      <div className="space-y-5">
        <div className="bg-red-50 border-2 border-red-600 p-3 text-sm font-bold text-red-700">
          🚫 You never collect money. Every order in this app is already paid in full. If a customer offers payment, politely decline and tell them to call the store.
        </div>
        <ol className="space-y-2">
          <Step n={1}>Your stops for today load automatically. Tap <b>Directions</b> for turn-by-turn, <b>Call</b> to reach the customer.</Step>
          <Step n={2}>Leaving the store with the order? Tap <b>🚚 Start delivery</b> — the customer gets a text that you're on the way.</Step>
          <Step n={3}>At the door, tap <b>📍 Arrived</b> (this stamps the time).</Step>
          <Step n={4}>Unload and let the customer inspect. Tap <b>✓ Mark DELIVERED</b>: check <b>"No damage confirmed"</b> — or if anything is damaged, take a <b>photo</b> instead.</Step>
          <Step n={5}>Have the customer <b>sign on your screen</b> with their finger. That closes the stop and texts them a confirmation.</Step>
          <Step n={6}>Nobody home, or they refuse it? Tap <b>"Customer not home / refused"</b> — the store gets alerted and reschedules. Never leave items unattended.</Step>
        </ol>
        <div className="text-sm text-gray-600">Problems on the road? Call the store: <b>(616) 410-0158</b>.</div>
      </div>
    </HelpShell>
  );
}
